import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {ScorecardsService} from 'client';
import {getService} from 'utilities';

import {
  applyScorecardCurrentSnapshotEnsure,
  applyScorecardSnapshotNotesUpdate,
  applyScorecardSnapshotSummaryUpdate,
  type ScorecardResponseWithSnapshotNotes,
} from './scorecardData';
import {scorecardDataKeys} from './scorecardQueryKeys';

export {scorecardDataKeys};

/** Available snapshot capture dates (newest first) for the stepper. */
export const useScorecardSnapshots = () =>
  useQuery({
    queryKey: scorecardDataKeys.snapshots(),
    queryFn: () => getService(ScorecardsService).scorecardSnapshotsApiV1ScorecardsSnapshotsGet(),
    staleTime: 5 * 60 * 1000,
  });

/** Presentation-ready scorecard for a snapshot (Current when omitted). */
export const useScorecards = (snapshotDt?: string | null) =>
  useQuery({
    queryKey: scorecardDataKeys.metrics(snapshotDt),
    queryFn: () =>
      getService(ScorecardsService).scorecardsApiV1ScorecardsGet(snapshotDt ?? undefined),
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateScorecardSnapshotNotes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({metricSnapshotId, notes}: {metricSnapshotId: number; notes: string}) =>
      getService(
        ScorecardsService
      ).updateScorecardSnapshotNotesApiV1ScorecardsSnapshotsSnapshotIdNotesPut(metricSnapshotId, {
        notes,
      }),
    onSuccess: (response) => {
      queryClient.setQueriesData<ScorecardResponseWithSnapshotNotes>(
        {queryKey: [...scorecardDataKeys.all, 'metrics']},
        (oldScorecard) => applyScorecardSnapshotNotesUpdate(oldScorecard, response)
      );
    },
  });
};

export const useEnsureCurrentScorecardSnapshot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (metricId: number) =>
      getService(
        ScorecardsService
      ).ensureCurrentScorecardMetricSnapshotApiV1ScorecardsMetricsMetricIdCurrentSnapshotPost(
        metricId
      ),
    onSuccess: (response) => {
      queryClient.setQueriesData<ScorecardResponseWithSnapshotNotes>(
        {queryKey: [...scorecardDataKeys.all, 'metrics']},
        (oldScorecard) => applyScorecardCurrentSnapshotEnsure(oldScorecard, response)
      );
    },
  });
};

export const useUpdateScorecardSnapshotSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      snapshotDt,
      tabId,
      summary,
    }: {
      snapshotDt: string;
      tabId: string;
      summary: string;
    }) =>
      getService(ScorecardsService).updateScorecardSnapshotSummaryApiV1ScorecardsSnapshotSummaryPut(
        {
          snapshot_dt: snapshotDt,
          tab_id: tabId,
          summary,
        }
      ),
    onSuccess: (response) => {
      queryClient.setQueriesData<ScorecardResponseWithSnapshotNotes>(
        {queryKey: [...scorecardDataKeys.all, 'metrics']},
        (oldScorecard) => applyScorecardSnapshotSummaryUpdate(oldScorecard, response)
      );
    },
  });
};
